import React, { useMemo } from "react";
import { formatSingleAmount } from "../../utils/currency";
import { TrendWeek, WeekVolume } from "./types";
import { fmtDay, SERIES_COLORS } from "./shared";

// Grouped per-currency weekly bars (HTML/CSS, no chart lib — consistent with
// the rest of the admin dashboard). Legend when ≥2 currencies; native title
// tooltips per bar; a <details> table view for exact numbers.
export function WeeklyVolumeChart({ weeks }: { weeks: WeekVolume[] }) {
  const currencies = useMemo(() => {
    const set = new Set<string>();
    weeks.forEach((w) => Object.keys(w.byCurrency).forEach((c) => set.add(c)));
    return Array.from(set).sort();
  }, [weeks]);
  const max = useMemo(
    () => Math.max(1, ...weeks.flatMap((w) => currencies.map((c) => w.byCurrency[c] || 0))),
    [weeks, currencies]
  );

  if (currencies.length === 0) {
    return <p className="muted-text adm-p-12">No paid requests in the last 12 weeks.</p>;
  }

  return (
    <div>
      {currencies.length >= 2 && (
        <div style={{ display: "flex", gap: 14, marginBottom: 6 }}>
          {currencies.map((c, i) => (
            <span key={c} className="adm-fs-72" style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 3,
                  background: SERIES_COLORS[Math.min(i, SERIES_COLORS.length - 1)],
                  display: "inline-block",
                }}
              />
              {c.toUpperCase()}
            </span>
          ))}
        </div>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: 6,
          height: 120,
          borderBottom: "1px solid var(--adm-line)",
          paddingBottom: 0,
        }}
      >
        {weeks.map((w) => (
          <div
            key={w.weekStart}
            className="adm-flex-1" style={{ display: "flex", alignItems: "flex-end", gap: 2, height: "100%" }}
          >
            {currencies.map((c, i) => {
              const v = w.byCurrency[c] || 0;
              return (
                <div
                  key={c}
                  title={`Week of ${fmtDay(w.weekStart)}: ${formatSingleAmount(v, c)} (${w.orders} paid request${w.orders === 1 ? "" : "s"} all currencies)`}
                  style={{
                    flex: 1,
                    height: `${Math.round((v / max) * 100)}%`,
                    minHeight: v > 0 ? 3 : 0,
                    background: SERIES_COLORS[Math.min(i, SERIES_COLORS.length - 1)],
                    borderRadius: "4px 4px 0 0",
                  }}
                />
              );
            })}
          </div>
        ))}
      </div>
      <div className="adm-mt-4" style={{ display: "flex", gap: 6 }}>
        {weeks.map((w, i) => (
          <div key={w.weekStart} className="muted-text adm-flex-1 adm-center" style={{ fontSize: "0.62rem" }}>
            {i % 2 === 0 ? fmtDay(w.weekStart).replace(/, \d{4}$/, "") : ""}
          </div>
        ))}
      </div>
      <details className="adm-mt-6">
        <summary className="muted-text adm-fs-72 adm-pointer">
          Show data table
        </summary>
        <table className="adm-table-light adm-table-compact adm-mt-6">
          <thead>
            <tr>
              <th>Week of</th>
              {currencies.map((c) => (
                <th key={c}>{c.toUpperCase()}</th>
              ))}
              <th>Requests</th>
            </tr>
          </thead>
          <tbody>
            {weeks.map((w) => (
              <tr key={w.weekStart}>
                <td>{fmtDay(w.weekStart)}</td>
                {currencies.map((c) => (
                  <td key={c}>
                    {formatSingleAmount(w.byCurrency[c] || 0, c)}
                  </td>
                ))}
                <td>{w.orders}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}

// Single-series weekly refund+dispute rate bars with the counts in tooltips.
export function RefundTrendChart({ weeks }: { weeks: TrendWeek[] }) {
  const max = Math.max(5, ...weeks.map((w) => w.ratePct || 0)); // ≥5% scale so tiny rates don't look alarming
  const any = weeks.some((w) => w.refunds > 0 || w.disputes > 0 || w.paidOrders > 0);
  if (!any) return <p className="muted-text adm-p-12">No paid activity in the last 12 weeks.</p>;
  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: 6,
          height: 90,
          borderBottom: "1px solid var(--adm-line)",
        }}
      >
        {weeks.map((w) => {
          const pctVal = w.ratePct || 0;
          return (
            <div
              key={w.weekStart}
              title={`Week of ${fmtDay(w.weekStart)}: ${w.refunds} refund${w.refunds === 1 ? "" : "s"}, ${w.disputes} dispute${w.disputes === 1 ? "" : "s"} / ${w.paidOrders} paid → ${w.ratePct === null ? "no paid orders" : `${w.ratePct}%`}`}
              style={{
                flex: 1,
                height: `${Math.round((pctVal / max) * 100)}%`,
                minHeight: pctVal > 0 ? 3 : 0,
                background: "#3b82f6",
                borderRadius: "4px 4px 0 0",
              }}
            />
          );
        })}
      </div>
      <div className="adm-mt-4" style={{ display: "flex", gap: 6 }}>
        {weeks.map((w, i) => (
          <div key={w.weekStart} className="muted-text adm-flex-1 adm-center" style={{ fontSize: "0.62rem" }}>
            {i % 2 === 0 ? fmtDay(w.weekStart).replace(/, \d{4}$/, "") : ""}
          </div>
        ))}
      </div>
      <details className="adm-mt-6">
        <summary className="muted-text adm-fs-72 adm-pointer">
          Show data table
        </summary>
        <table className="adm-table-light adm-table-compact adm-mt-6">
          <thead>
            <tr>
              <th>Week of</th>
              <th>Refunds</th>
              <th>Disputes</th>
              <th>Paid orders</th>
              <th>Rate</th>
            </tr>
          </thead>
          <tbody>
            {weeks.map((w) => (
              <tr key={w.weekStart}>
                <td>{fmtDay(w.weekStart)}</td>
                <td>{w.refunds}</td>
                <td>{w.disputes}</td>
                <td>{w.paidOrders}</td>
                <td>{w.ratePct === null ? "—" : `${w.ratePct}%`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}
